/**
 * Route subtree mounted at `/w/:slug/agents`, `/w/:slug/channels`,
 * `/w/:slug/agentops` from `App.tsx`. Pages are code-split via
 * `React.lazy` + `<Suspense>` so the agents bundle doesn't bloat the
 * initial dashboard load.
 *
 * Per `aidlc-docs/construction/plans/agents-runtime-m7-v0-plan.md § CG-v0-7`.
 */

import { lazy, Suspense } from "react";
import { Route } from "react-router";

import { RouteFallback } from "../components/route-fallback";

const AgentsListPage = lazy(() =>
  import("./pages/AgentsListPage").then((m) => ({ default: m.AgentsListPage })),
);
const AgentEditorPage = lazy(() =>
  import("./pages/AgentEditorPage").then((m) => ({ default: m.AgentEditorPage })),
);
const AgentVersionsPage = lazy(() =>
  import("./pages/AgentVersionsPage").then((m) => ({ default: m.AgentVersionsPage })),
);
const ChannelsPage = lazy(() =>
  import("./pages/ChannelsPage").then((m) => ({ default: m.ChannelsPage })),
);
const ChannelCreatePage = lazy(() =>
  import("./pages/ChannelCreatePage").then((m) => ({ default: m.ChannelCreatePage })),
);
const AgentOpsPage = lazy(() =>
  import("./pages/AgentOpsPage").then((m) => ({ default: m.AgentOpsPage })),
);
const AgentOpsRunDetailPage = lazy(() =>
  import("./pages/AgentOpsRunDetailPage").then((m) => ({
    default: m.AgentOpsRunDetailPage,
  })),
);
const AgentOpsSegmentDetailPage = lazy(() =>
  import("./pages/AgentOpsSegmentDetailPage").then((m) => ({
    default: m.AgentOpsSegmentDetailPage,
  })),
);
const ConversationsPage = lazy(() =>
  import("./pages/ConversationsPage").then((m) => ({ default: m.ConversationsPage })),
);
const ChannelDetailPage = lazy(() =>
  import("./pages/ChannelDetailPage").then((m) => ({ default: m.ChannelDetailPage })),
);

function withSuspense(node: React.ReactElement): React.ReactElement {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>;
}

/**
 * Returns the JSX route definitions to drop inside the workspace-scoped
 * `<Route path="/w/:slug">` block in `App.tsx`.
 */
export function agentsRoutes() {
  return (
    <>
      <Route path="agents" element={withSuspense(<AgentsListPage />)} />
      <Route path="agents/new" element={withSuspense(<AgentEditorPage />)} />
      <Route path="agents/:agentId" element={withSuspense(<AgentEditorPage />)} />
      <Route path="agents/:agentId/versions" element={withSuspense(<AgentVersionsPage />)} />
      <Route
        path="agents/:agentId/versions/:versionId"
        element={withSuspense(<AgentEditorPage />)}
      />
      <Route path="channels" element={withSuspense(<ChannelsPage />)} />
      <Route path="channels/new" element={withSuspense(<ChannelCreatePage />)} />
      <Route path="channels/:bindingId" element={withSuspense(<ChannelDetailPage />)} />
      <Route path="agentops" element={withSuspense(<AgentOpsPage />)} />
      <Route path="agentops/runs/:runId" element={withSuspense(<AgentOpsRunDetailPage />)} />
      <Route
        path="agentops/segments/:handoffId"
        element={withSuspense(<AgentOpsSegmentDetailPage />)}
      />
      <Route path="conversations" element={withSuspense(<ConversationsPage />)} />
    </>
  );
}
